import { RatingsStatsPopover } from "./RatingsStatsPopover/index.tsx";
import { SkillsBlock } from "./SkillsBlock.tsx";
import { helpers } from "../util/helpers.ts";
import { createMobileCrossTabHref } from "../util/mobileCrossTabLink.ts";
import { toWorker } from "../util/toWorker.ts";
import type { Player, PlayerInjury } from "../../../../common/types.ts";
import { InjuryIcon } from "./InjuryIcon.tsx";
import { SeasonIcons } from "./SeasonIcons.tsx";
import { CountryFlag } from "./CountryFlag.tsx";
import { useEffect, useState, type MouseEvent } from "react";
import { useLocal, useLocalPartial } from "../util/local.ts";
import {
  formatPlayerName,
  parseLegacyPlayerName,
  translatePlayerName,
} from "../util/playerNames.ts";
import { translateTeamShortName } from "../util/teamNames.ts";
import { PositionText } from "../util/positionNames.tsx";

type Props = {
  awards?: Player["awards"];
  awardsSeason?: number;
  count?: number;
  jerseyNumber?: string;
  defaultWatch?: number;
  injury?: PlayerInjury & {
    playingThrough?: boolean;
  };
  pos?: string;
  pid?: number;
  playerHref?: string;
  playerCardLinkPid?: number;
  onNameClick?: (event: MouseEvent<HTMLAnchorElement>) => void;

  abbrev?: string;
  tid?: number;

  // season is passed to RatingsStatsPopover, where it's used to determine whether to show a historical season's data. Also team link
  season?: number;

  skills?: string[];
  style?: {
    [key: string]: string;
  };

  firstName?: string;
  lastName?: string;
  firstNameShort?: string;

  // For when we supply a pid, but we want no link to player page.
  disableNameLink?: boolean;

  // Allow overriding settings, for places where we're sure there is always room
  fullNames?: boolean;

  // Pass to override firstName and lastName
  legacyName?: string;

  // Override the alwaysShowCountry feature to always be disabled, in the case where we know the country is displayed elsewhere nearby already
  neverShowCountry?: boolean;

  // For table "Name" columns, keep the cell focused on identity only.
  nameOnly?: boolean;
};

const getFirstLastNames = (props: Props) => {
  let firstName: string;
  let lastName: string;
  if (props.legacyName) {
    const parts = parseLegacyPlayerName(props.legacyName);
    firstName = parts.firstName;
    lastName = parts.lastName;
  } else if (props.firstName !== undefined && props.lastName !== undefined) {
    firstName = props.firstName;
    lastName = props.lastName;
  } else {
    throw new Error("Missing firstName/lastName/legacyName");
  }

  return {
    firstName,
    lastName,
  };
};

export const CountBadge = ({ count }: { count: number }) => {
  if (count > 1) {
    return (
      <div className="ms-1">
        <span className="badge bg-secondary align-text-bottom">{count}</span>
      </div>
    );
  }

  return null;
};

const CountryFlagPid = ({ className, pid }: { className?: string; pid: number }) => {
  const [country, setCountry] = useState<undefined | string>();

  useEffect(() => {
    let mounted = true;
    (async () => {
      const country = await toWorker("main", "getBornLoc", pid);

      if (mounted) {
        setCountry(country);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [pid]);

  return <CountryFlag className={className} country={country ?? "Unknown"} />;
};

export const PlayerNameLabels = (props: Props) => {
  const localState = useLocalPartial(["alwaysShowCountry", "fullNames"]);
  const teamInfoCache = useLocal((state) => state.teamInfoCache);
  const alwaysShowCountry = localState.alwaysShowCountry;
  const fullNames = localState.fullNames || props.fullNames;

  const {
    abbrev,
    awards,
    awardsSeason,
    count,
    disableNameLink,
    injury,
    jerseyNumber,
    nameOnly,
    neverShowCountry,
    pid,
    playerHref,
    playerCardLinkPid,
    pos,
    season,
    skills,
    style,
    tid,
    defaultWatch,
  } = props;
  const displayFullNames = fullNames || nameOnly;

  let { firstName, lastName } = getFirstLastNames(props);
  const translation = translatePlayerName(firstName, lastName);
  const usingLocalizedName =
    translation.firstName !== firstName || translation.lastName !== lastName;
  firstName = translation.firstName;
  lastName = translation.lastName;
  // When a localized full name is used (CJK), there's no first/last split,
  // so suppress the mobile "first initial" rendering — `firstNameShort` is
  // English (e.g. "K.") and would render alongside the localized fullname.
  const firstNameShort = usingLocalizedName ? undefined : props.firstNameShort;

  const name = usingLocalizedName ? (
    <>{lastName}</>
  ) : (
    <>
      {firstNameShort && !displayFullNames ? (
        <>
          <span className="d-inline-block d-sm-none">{firstNameShort}</span>
          <span className="d-none d-sm-inline">{firstName}</span>
        </>
      ) : (
        firstName
      )}{" "}
      {lastName}
    </>
  );

  const nameBlock =
    !disableNameLink && pid !== undefined ? (
      <a
        data-player-card-link-pid={playerCardLinkPid}
        href={playerHref ?? createMobileCrossTabHref(helpers.leagueUrl(["player", pid]))}
        onClick={props.onNameClick}
      >
        {name}
      </a>
    ) : (
      name
    );

  if (nameOnly) {
    return <span style={style}>{nameBlock}</span>;
  }

  const nameLabelsBlock = (
    <span style={style}>
      {Object.hasOwn(props, "jerseyNumber") ? (
        <span
          className={`text-body-secondary jersey-number-name text-start${
            !displayFullNames ? " d-none d-sm-inline-block" : ""
          }`}
        >
          {jerseyNumber}
        </span>
      ) : null}
      {typeof pos === "string" ? (
        <>
          <PositionText pos={pos} />{" "}
        </>
      ) : null}
      {nameBlock}
      <InjuryIcon injury={injury} />
      <SkillsBlock host="player-name-labels" skills={skills} />
      {pid !== undefined ? (
        <RatingsStatsPopover
          pid={pid}
          season={season}
          defaultWatch={defaultWatch}
          disableNameLink={disableNameLink}
          playerHref={playerHref}
        />
      ) : null}
      {abbrev !== undefined && tid !== undefined ? (
        <a
          href={createMobileCrossTabHref(helpers.leagueUrl(["roster", `${abbrev}_${tid}`, season]))}
          className="ms-1"
        >
          {translateTeamShortName(teamInfoCache[tid], abbrev)}
        </a>
      ) : null}
      {alwaysShowCountry && !neverShowCountry && pid !== undefined ? (
        <CountryFlagPid pid={pid} className="ms-2" />
      ) : null}
    </span>
  );

  if (awards) {
    return (
      <div className="d-flex">
        {nameLabelsBlock}
        <div className="ms-auto">
          <SeasonIcons className="ms-1" awards={awards} season={awardsSeason} playoffs />
          <SeasonIcons className="ms-1" awards={awards} season={awardsSeason} />
        </div>
      </div>
    );
  }

  if (count !== undefined) {
    return (
      <div className="d-flex">
        <div className="me-auto">{nameLabelsBlock}</div>
        <CountBadge count={count} />
      </div>
    );
  }

  return nameLabelsBlock;
};

export const wrappedPlayerNameLabels = (props: Props) => {
  const { firstName, lastName } = getFirstLastNames(props);
  const localizedName = formatPlayerName(firstName, lastName);
  const englishName = `${firstName} ${lastName}`.trim();

  return {
    value: <PlayerNameLabels {...props} />,
    sortValue: localizedName === englishName ? `${lastName} ${firstName}` : localizedName,
    searchValue: localizedName === englishName ? englishName : `${localizedName} ${englishName}`,
  };
};
